import React, { useState } from "react";
import { UploadCloud, X, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/site";

// Reusable image upload widget for admin.
// Modes:
//  - "single": returns {id, url} via onChange
//  - "multi":  returns arrays [ids, urls] via onChange
export default function ImageUpload({ mode = "single", scope = "admin", value, onChange, label = "Bilde" }) {
  const [uploading, setUploading] = useState(false);

  const upload = async (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    setUploading(true);
    const uploaded = [];
    for (const f of arr) {
      try {
        const fd = new FormData();
        fd.append("file", f); fd.append("scope", scope);
        const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ id: r.data.id, url: `${process.env.REACT_APP_BACKEND_URL}${r.data.url}` });
      } catch (e) {
        toast.error("Opplasting feilet");
      }
    }
    if (mode === "single") {
      if (uploaded[0]) onChange(uploaded[0]);
    } else {
      const existing = Array.isArray(value) ? value : [];
      onChange([...existing, ...uploaded]);
    }
    setUploading(false);
  };

  if (mode === "single") {
    const preview = value?.url || value?.id ? (value.url || `${process.env.REACT_APP_BACKEND_URL}/api/files/${value.id}`) : null;
    return (
      <div>
        <div className="text-[11px] uppercase tracking-wider text-[#555E68] mb-1.5">{label}</div>
        <div className="flex gap-3 items-start">
          {preview && (
            <div className="relative w-24 h-24 rounded-md overflow-hidden border border-[#E2DDD5] shrink-0">
              <img src={preview} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => onChange(null)} className="absolute top-0.5 right-0.5 w-6 h-6 rounded-full bg-black/70 text-white grid place-items-center" data-testid="img-single-remove"><X size={12}/></button>
            </div>
          )}
          <label className={`flex-1 rounded-md border border-dashed border-[#C7BFAE] bg-[#FAF8F5] px-3.5 py-4 cursor-pointer hover:bg-[#F1EDE3] flex items-center gap-2 ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
            {uploading ? <Loader2 size={16} className="animate-spin text-[#1B3626]"/> : <UploadCloud size={16} className="text-[#1B3626]" />}
            <span className="text-sm text-[#555E68]">{uploading ? "Laster opp..." : preview ? "Bytt bilde" : "Velg et bilde"}</span>
            <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files)} data-testid="img-single-file" />
          </label>
        </div>
        <div className="mt-2 text-[11px] text-[#7f7a68] flex items-center gap-1"><ExternalLink size={11}/> Eller lim inn en URL i tekstfeltet under</div>
      </div>
    );
  }

  const items = Array.isArray(value) ? value : [];
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-[#555E68] mb-1.5">{label} ({items.length})</div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-2">
        {items.map((it, i) => (
          <div key={it.id || it.url || i} className="relative aspect-square rounded-md overflow-hidden border border-[#E2DDD5]">
            <img src={it.url || `${process.env.REACT_APP_BACKEND_URL}/api/files/${it.id}`} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="absolute top-0.5 right-0.5 w-6 h-6 rounded-full bg-black/70 text-white grid place-items-center" data-testid={`img-multi-remove-${i}`}><X size={12}/></button>
          </div>
        ))}
        <label className={`aspect-square rounded-md border border-dashed border-[#C7BFAE] bg-[#FAF8F5] hover:bg-[#F1EDE3] cursor-pointer grid place-items-center ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
          {uploading ? <Loader2 size={16} className="animate-spin text-[#1B3626]"/> : <UploadCloud size={18} className="text-[#1B3626]"/>}
          <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => upload(e.target.files)} data-testid="img-multi-file" />
        </label>
      </div>
    </div>
  );
}
